import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  message,
  Space,
  Tag,
  Typography,
  Tabs,
  Row,
  Col,
  Statistic,
  Progress,
  Input,
} from 'antd';
import {
  BarChartOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  BankOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchTransactionReport,
  fetchSummaryReport,
  fetchSettlementReport,
  fetchDisputeReport,
  fetchFeeReport,
  exportReport,
  fetchExports,
  downloadReport,
} from '../store/slices/reportSlice';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const ReportsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [exportForm] = Form.useForm();
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);

  const {
    transactionReport,
    summaryReport,
    settlementReport,
    disputeReport,
    feeReport,
    exports,
    exporting,
    downloading,
    error,
  } = useAppSelector((state) => state.report);

  useEffect(() => {
    handleFetchReports();
    dispatch(fetchExports({ page: 1, limit: 20 }));
  }, []);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleFetchReports = () => {
    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');

    dispatch(fetchTransactionReport({ startDate, endDate }));
    dispatch(fetchSummaryReport({ startDate, endDate }));
    dispatch(fetchSettlementReport({ startDate, endDate }));
    dispatch(fetchDisputeReport({ startDate, endDate }));
    dispatch(fetchFeeReport({ startDate, endDate }));
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleExportReport = async (values: any) => {
    try {
      await dispatch(
        exportReport({
          type: values.type,
          format: values.format,
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
          filters: values.filters ? JSON.parse(values.filters) : undefined,
        })
      ).unwrap();
      message.success('导出任务已创建，请稍后在导出管理中下载');
      setExportModalVisible(false);
      exportForm.resetFields();
      dispatch(fetchExports({ page: 1, limit: 20 }));
    } catch (err: any) {
      message.error(err || '创建导出任务失败');
    }
  };

  const handleDownload = async (id: string, filename: string) => {
    try {
      await dispatch(downloadReport({ id, filename })).unwrap();
      message.success('报表下载成功');
    } catch (err: any) {
      message.error(err || '下载失败');
    }
  };

  const exportColumns: ColumnsType<any> = [
    {
      title: '导出ID',
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text.substring(0, 16)}...
        </Text>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeMap: Record<string, string> = {
          transactions: '交易报表',
          orders: '订单报表',
          refunds: '退款报表',
          settlements: '结算报表',
          disputes: '争议报表',
        };
        return typeMap[type] || type;
      },
    },
    {
      title: '格式',
      dataIndex: 'format',
      key: 'format',
      render: (format: string) => <Tag>{format.toUpperCase()}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<
          string,
          { label: string; color: string; icon: React.ReactNode }
        > = {
          pending: {
            label: '等待中',
            color: 'default',
            icon: <ClockCircleOutlined />,
          },
          processing: {
            label: '处理中',
            color: 'processing',
            icon: <ClockCircleOutlined />,
          },
          completed: {
            label: '已完成',
            color: 'success',
            icon: <CheckCircleOutlined />,
          },
          failed: {
            label: '失败',
            color: 'error',
            icon: <CloseCircleOutlined />,
          },
        };
        const config = statusMap[status] || {
          label: status,
          color: 'default',
          icon: null,
        };
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number, record: any) => {
        if (record.status === 'completed') {
          return <Progress percent={100} size="small" status="success" />;
        } else if (record.status === 'failed') {
          return <Progress percent={progress} size="small" status="exception" />;
        } else if (record.status === 'processing') {
          return <Progress percent={progress} size="small" status="active" />;
        }
        return <Progress percent={0} size="small" />;
      },
    },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (size: string) => size || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '完成时间',
      dataIndex: 'completed_at',
      key: 'completed_at',
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          {record.status === 'completed' && record.download_url && (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() =>
                handleDownload(record.id, `report_${record.type}_${record.format}`)
              }
              loading={downloading}
            >
              下载
            </Button>
          )}
          {record.status === 'failed' && (
            <Text type="danger" style={{ fontSize: 12 }}>
              导出失败
            </Text>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          报表中心
        </Title>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            allowClear={false}
          />
          <Button icon={<ReloadOutlined />} onClick={handleFetchReports}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => setExportModalVisible(true)}
          >
            导出报表
          </Button>
        </Space>
      </div>

      <Card>
        <Tabs defaultActiveKey="transaction">
          {/* Transaction Report */}
          <TabPane
            tab={
              <span>
                <BarChartOutlined />
                交易报表
              </span>
            }
            key="transaction"
          >
            {transactionReport.data ? (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={6}>
                    <Card size="small">
                      <Statistic
                        title="总交易数"
                        value={transactionReport.data.summary?.total_transactions ?? 0}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card size="small">
                      <Statistic
                        title="总金额"
                        value={(transactionReport.data.summary?.total_amount ?? 0) / 100}
                        precision={2}
                        suffix="USD"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card size="small">
                      <Statistic
                        title="成功交易"
                        value={transactionReport.data.summary?.successful_transactions ?? 0}
                        valueStyle={{ color: '#3f8600' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Card size="small">
                      <Statistic
                        title="成功率"
                        value={transactionReport.data.summary?.success_rate ?? 0}
                        precision={2}
                        suffix="%"
                        valueStyle={{
                          color:
                            (transactionReport.data.summary?.success_rate ?? 0) >= 95
                              ? '#3f8600'
                              : '#faad14',
                        }}
                      />
                    </Card>
                  </Col>
                </Row>

                <Table
                  dataSource={transactionReport.data.transactions || []}
                  columns={[
                    {
                      title: '日期',
                      dataIndex: 'date',
                      key: 'date',
                    },
                    {
                      title: '交易数',
                      dataIndex: 'count',
                      key: 'count',
                    },
                    {
                      title: '金额',
                      dataIndex: 'amount',
                      key: 'amount',
                      render: (amount: number) => `USD ${(amount / 100).toFixed(2)}`,
                    },
                  ]}
                  loading={transactionReport.loading}
                  pagination={false}
                />
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">请选择日期范围查看报表</Text>
              </div>
            )}
          </TabPane>

          {/* Summary Report */}
          <TabPane
            tab={
              <span>
                <FileTextOutlined />
                摘要报表
              </span>
            }
            key="summary"
          >
            {summaryReport.data ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Card title="收入统计" size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Statistic
                        title="总收入"
                        value={(summaryReport.data.revenue?.total ?? 0) / 100}
                        precision={2}
                        suffix={summaryReport.data.revenue?.currency || 'USD'}
                      />
                      <div>
                        <Text strong>按币种统计：</Text>
                        {summaryReport.data.revenue?.by_currency?.map((item: any) => (
                          <div key={item.currency} style={{ marginTop: 8 }}>
                            <Tag>{item.currency}</Tag>
                            <Text>{(item.amount / 100).toFixed(2)}</Text>
                          </div>
                        ))}
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card title="交易统计" size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Statistic
                        title="总交易数"
                        value={summaryReport.data.transactions?.count ?? 0}
                      />
                      <div>
                        <Text strong>按状态统计：</Text>
                        {summaryReport.data.transactions?.by_status?.map((item: any) => (
                          <div key={item.status} style={{ marginTop: 8 }}>
                            <Tag>{item.status}</Tag>
                            <Text>{item.count}</Text>
                          </div>
                        ))}
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card title="退款统计" size="small">
                    <Statistic
                      title="退款笔数"
                      value={summaryReport.data.refunds?.count ?? 0}
                    />
                    <Statistic
                      title="退款金额"
                      value={(summaryReport.data.refunds?.amount ?? 0) / 100}
                      precision={2}
                      suffix="USD"
                      style={{ marginTop: 16 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12}>
                  <Card title="争议统计" size="small">
                    <Statistic
                      title="争议笔数"
                      value={summaryReport.data.disputes?.count ?? 0}
                    />
                    <Statistic
                      title="争议金额"
                      value={(summaryReport.data.disputes?.amount ?? 0) / 100}
                      precision={2}
                      suffix="USD"
                      style={{ marginTop: 16 }}
                    />
                  </Card>
                </Col>
              </Row>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">请选择日期范围查看报表</Text>
              </div>
            )}
          </TabPane>

          {/* Settlement Report */}
          <TabPane
            tab={
              <span>
                <BankOutlined />
                结算报表
              </span>
            }
            key="settlement"
          >
            {settlementReport.data ? (
              <div>结算报表数据展示（根据后端数据结构调整）</div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">请选择日期范围查看报表</Text>
              </div>
            )}
          </TabPane>

          {/* Dispute Report */}
          <TabPane
            tab={
              <span>
                <CloseCircleOutlined />
                争议报表
              </span>
            }
            key="dispute"
          >
            {disputeReport.data ? (
              <div>争议报表数据展示（根据后端数据结构调整）</div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">请选择日期范围查看报表</Text>
              </div>
            )}
          </TabPane>

          {/* Fee Report */}
          <TabPane
            tab={
              <span>
                <DollarOutlined />
                费用报表
              </span>
            }
            key="fee"
          >
            {feeReport.data ? (
              <div>费用报表数据展示（根据后端数据结构调整）</div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">请选择日期范围查看报表</Text>
              </div>
            )}
          </TabPane>

          {/* Export Management */}
          <TabPane
            tab={
              <span>
                <DownloadOutlined />
                导出管理
              </span>
            }
            key="exports"
          >
            <Table
              columns={exportColumns}
              dataSource={exports.data || []}
              loading={exports.loading}
              rowKey="id"
              locale={{ emptyText: '暂无数据' }}
              pagination={{
                total: exports.meta?.total || 0,
                current: exports.meta?.current_page || 1,
                pageSize: exports.meta?.per_page || 20,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => {
                  dispatch(fetchExports({ page, limit: pageSize }));
                },
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Export Report Modal */}
      <Modal
        title="导出报表"
        open={exportModalVisible}
        onCancel={() => {
          setExportModalVisible(false);
          exportForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={exportForm} layout="vertical" onFinish={handleExportReport}>
          <Form.Item
            label="报表类型"
            name="type"
            rules={[{ required: true, message: '请选择报表类型' }]}
          >
            <Select placeholder="请选择">
              <Select.Option value="transactions">交易报表</Select.Option>
              <Select.Option value="orders">订单报表</Select.Option>
              <Select.Option value="refunds">退款报表</Select.Option>
              <Select.Option value="settlements">结算报表</Select.Option>
              <Select.Option value="disputes">争议报表</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="导出格式"
            name="format"
            rules={[{ required: true, message: '请选择导出格式' }]}
          >
            <Select placeholder="请选择">
              <Select.Option value="csv">CSV</Select.Option>
              <Select.Option value="excel">Excel</Select.Option>
              <Select.Option value="pdf">PDF</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="筛选条件（JSON）" name="filters">
            <Input.TextArea
              rows={4}
              placeholder='{"status": "completed", "currency": "USD"}'
            />
          </Form.Item>

          <div style={{ marginBottom: 16, padding: 12, background: '#f0f2f5' }}>
            <Text>
              日期范围：{dateRange[0].format('YYYY-MM-DD')} 至{' '}
              {dateRange[1].format('YYYY-MM-DD')}
            </Text>
          </div>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setExportModalVisible(false);
                  exportForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={exporting}>
                创建导出任务
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReportsPage;
